import { api } from './client';
import type { MailListItem, MailDetail, MailUser, MailFolder, Paginated } from '@/types';

interface ComposePayload {
  subject?: string; body?: string;
  to_ids?: number[]; cc_ids?: number[]; external_to?: string;
  importance?: 'normal' | 'high'; is_draft?: boolean;
  related_contract?: number | null; related_object?: number | null;
}

function toForm(p: ComposePayload, files?: File[]): FormData {
  const fd = new FormData();
  if (p.subject) fd.append('subject', p.subject);
  if (p.body) fd.append('body', p.body);
  if (p.to_ids?.length) fd.append('to_ids', p.to_ids.join(','));
  if (p.cc_ids?.length) fd.append('cc_ids', p.cc_ids.join(','));
  if (p.external_to) fd.append('external_to', p.external_to);
  if (p.importance) fd.append('importance', p.importance);
  fd.append('is_draft', String(p.is_draft ?? false));
  if (p.related_contract) fd.append('related_contract', String(p.related_contract));
  if (p.related_object) fd.append('related_object', String(p.related_object));
  (files ?? []).forEach((f) => fd.append('files', f));
  return fd;
}

export const mailApi = {
  async list(folder: MailFolder): Promise<MailListItem[]> {
    const { data } = await api.get<Paginated<MailListItem>>('/mail/', { params: { folder } });
    return data.results ?? (data as unknown as MailListItem[]);
  },
  async get(id: number): Promise<MailDetail> {
    const { data } = await api.get<MailDetail>(`/mail/${id}/`);
    return data;
  },
  async compose(payload: ComposePayload, files?: File[]): Promise<MailDetail> {
    const { data } = await api.post<MailDetail>('/mail/', toForm(payload, files),
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  },
  async send(id: number): Promise<MailDetail> {
    const { data } = await api.post<MailDetail>(`/mail/${id}/send/`);
    return data;
  },
  async reply(id: number, body: string, replyAll = false, files?: File[]): Promise<MailDetail> {
    const fd = new FormData();
    fd.append('body', body);
    fd.append('reply_all', String(replyAll));
    (files ?? []).forEach((f) => fd.append('files', f));
    const { data } = await api.post<MailDetail>(`/mail/${id}/reply/`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  },
  async forward(id: number, opts: {
    to_ids?: number[]; cc_ids?: number[]; external_to?: string;
    note?: string; resolution?: string;
  }, files?: File[]): Promise<MailDetail> {
    const fd = new FormData();
    if (opts.to_ids?.length) fd.append('to_ids', opts.to_ids.join(','));
    if (opts.cc_ids?.length) fd.append('cc_ids', opts.cc_ids.join(','));
    if (opts.external_to) fd.append('external_to', opts.external_to);
    if (opts.note) fd.append('note', opts.note);
    if (opts.resolution) fd.append('resolution', opts.resolution);
    (files ?? []).forEach((f) => fd.append('files', f));
    const { data } = await api.post<MailDetail>(`/mail/${id}/forward/`, fd,
      { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  },
  async markRead(id: number) { await api.post(`/mail/${id}/read/`); },
  async markUnread(id: number) { await api.post(`/mail/${id}/unread/`); },
  async toggleImportant(id: number): Promise<boolean> {
    const { data } = await api.post<{ is_important: boolean }>(`/mail/${id}/important/`);
    return data.is_important;
  },
  async toggleArchive(id: number): Promise<boolean> {
    const { data } = await api.post<{ is_archived: boolean }>(`/mail/${id}/archive/`);
    return data.is_archived;
  },
  async remove(id: number) { await api.delete(`/mail/${id}/`); },
  async unreadCount(): Promise<number> {
    const { data } = await api.get<{ unread: number }>('/mail/unread-count/');
    return data.unread;
  },
  async contacts(): Promise<MailUser[]> {
    const { data } = await api.get<MailUser[]>('/mail/contacts/');
    return data;
  },
};
